/**
 * Pure helpers that normalize the raw `user_progress.data` JSONB blob
 * into the shape AppContext's React state expects.
 *
 * Extracted from src/contexts/AppContext.jsx so the field-mapping logic
 * can be unit-tested without rendering the full AppProvider + mocking
 * Supabase. Same pattern as the other src/logic/*.js extractions.
 *
 * The field-mapping is historically messy because three write paths
 * have each chosen a slightly different shape:
 *
 *   1. Vanilla src/main.js writes camelCase keys at the top level of
 *      data (currentWeek, currentYear, weekTrainedDays, total, streak,
 *      consentDate, path: 'relax' | 'reconnect' | 'strengthen', ...).
 *   2. React PathAssessment writes user_path: 'A' | 'B' to data.
 *   3. React flags (language_selected, consent_complete, onboarding_
 *      complete, breathing_discovery_complete, path_b_orientation_
 *      complete) are written as snake_case booleans at the top level.
 *
 * The normalizer prefers the newer/React fields when present and falls
 * back to the legacy vanilla fields so existing users are not disturbed.
 */

/**
 * The shape AppContext uses when there is no authenticated user, or
 * when the user has no Supabase row yet. Matches the keys emitted by
 * normalizeUserProgress() so the React state is always the same shape.
 */
export const DEFAULT_PROGRESS = {
  current_year: 1,
  current_week: 1,
  current_day: 1,
  current_day_in_week: 1,
  sessions_completed_this_week: 0,
  total_sessions_completed: 0,
  streak_days: 0,
  last_session_at: null,
  path: 'A',
  language_selected: false,
  consent_complete: false,
  onboarding_complete: false,
  breathing_discovery_complete: false,
  path_b_orientation_complete: false,
  sound_enabled: false,
  haptic_enabled: true,
  music_enabled: true,
  theme: 'rose',
  cycle_last_period_start: null,
  cycle_length: null,
  cycle_period_length: null,
  menopause_stage: null,
  life_stage: null,
  user_intent: 'unspecified',
  fl_signals: {},
  // Daily libido / desire signal. { [iso_date]: 0-3 }. The user logs
  // a single value per day from /body. Visible on every life stage so
  // it works for cycling, peri, and menopause users alike. The 0-3
  // scale matches the body-letter symptom palette.
  libido_log: {},
};

/** Themes the user can pick from in Profile. Anything else is coerced to 'rose'. */
export const THEMES = ['rose', 'sage', 'twilight'];

/**
 * Self-declared menopause stage. `null` means the user has not picked
 * a stage yet (no UI surfaced) so the cycle UI behaves exactly as it
 * does today for users who never see the stage selector. `unsure`
 * is an explicit answer (vs. `null` which is "not asked"), so the
 * tile collapses to summary mode after the user picks it.
 */
export const MENOPAUSE_STAGES = ['pre', 'peri', 'meno', 'post', 'unsure'];

/**
 * Life stage vocabulary written by the onboarding PersonalizeStep. This is a
 * separate field (`life_stage`) from the behavioural `menopause_stage`; the two
 * are bridged by resolveMenopauseStage so an onboarding answer is never orphaned.
 */
export const LIFE_STAGES = ['cycling', 'perimenopause', 'menopause', 'postmenopause', 'unspecified'];

/**
 * Map an onboarding `life_stage` value to the behavioural `menopause_stage`.
 * Tolerates the legacy stray 'peri' value once written by OnboardingFlow.
 */
const LIFE_STAGE_TO_MENOPAUSE = {
  cycling: 'pre',
  perimenopause: 'peri',
  peri: 'peri',
  menopause: 'meno',
  postmenopause: 'post',
  unspecified: 'unsure',
};

/**
 * Resolve the behavioural menopause stage from a raw data blob. An explicit
 * `menopause_stage` (set via the Profile MenopauseStageTile) always wins;
 * otherwise the onboarding `life_stage` answer is mapped in, so picking a stage
 * at onboarding actually drives the cycle / body / tracker UI. Returns null when
 * neither is set, preserving the "not asked, behave as before" default.
 *
 * @param {Object|null|undefined} d - The raw `data` column payload.
 * @returns {string|null} One of MENOPAUSE_STAGES, or null.
 */
export function resolveMenopauseStage(d) {
  if (!d || typeof d !== 'object') return null;
  if (MENOPAUSE_STAGES.includes(d.menopause_stage)) return d.menopause_stage;
  return LIFE_STAGE_TO_MENOPAUSE[d.life_stage] || null;
}

/**
 * Normalize a raw user_progress.data blob into the React state shape.
 * Returns null when the input is empty so the caller can decide
 * whether to leave React state at DEFAULT_PROGRESS.
 *
 * @param {Object|null|undefined} raw - The raw `data` column payload.
 * @returns {Object|null} The normalized userProgress shape, or null
 *   if raw is falsy / empty.
 */
export function normalizeUserProgress(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw;

  return {
    current_year: d.currentYear || d.current_year || 1,
    current_week: d.currentWeek || d.current_week || 1,
    current_day: d.current_day || 1,
    current_day_in_week: d.current_day_in_week || 1,
    sessions_completed_this_week:
      d.weekTrainedDays || d.sessions_completed_this_week || 0,
    total_sessions_completed: d.total || d.total_sessions_completed || 0,
    streak_days: d.streak || d.streak_days || 0,
    last_session_at: d.last_session_at || null,
    // React PathAssessment writes user_path: 'A' | 'B' to the bb7 blob.
    // Legacy vanilla flow writes data.path: 'relax' | 'reconnect' |
    // 'strengthen'. Prefer the React field when present so React-
    // onboarded users get a real path value; fall back to the legacy
    // field so existing users are not disturbed. Vanilla 'relax' maps
    // to React Path B (hypertonic/release-first); everything else
    // (incl. missing path) defaults to Path A.
    path:
      d.user_path === 'A' || d.user_path === 'B'
        ? d.user_path
        : d.path === 'relax'
          ? 'B'
          : 'A',
    // LanguageSelector writes language_selected: true after the user
    // picks a card. AppShell gates on this field — if it is not true,
    // LanguageSelector runs before anything else. Legacy users (who
    // never saw LanguageSelector) will have undefined → falsy → see
    // the selector once on their next login. That is the correct
    // one-time behaviour.
    language_selected: !!d.language_selected,
    // consent_complete is either the new React flag or the legacy
    // vanilla timestamp consentDate (any truthy string means consented).
    consent_complete: !!(d.consent_complete || d.consentDate),
    // Explicit flag, or implicit: path is set and week mode has begun.
    onboarding_complete: !!(
      d.onboarding_complete ||
      (d.path && (d.currentWeek || d.current_week))
    ),
    breathing_discovery_complete: !!d.breathing_discovery_complete,
    path_b_orientation_complete: !!d.path_b_orientation_complete,
    // Audio cues for session complete / diary save / feeling save.
    // Default off — the app is meditative by nature and users in
    // public spaces need opt-in before we play anything. Toggled by
    // Profile's sound switch; AppContext mirrors this to the runtime
    // Web Audio gate via setSoundEnabled().
    sound_enabled: !!d.sound_enabled,
    // Haptic feedback (navigator.vibrate) on diary save / session
    // complete. Default ON because vibration is silent and users
    // expect tactile confirmation; can be disabled in Profile for
    // people in shared beds, etc.
    haptic_enabled: d.haptic_enabled === false ? false : true,
    // Background music during exercise sessions. Default ON (only
    // an explicit false disables) so a fresh user gets the soft
    // looping track without a config step. Falls silent when no
    // /audio/music/*.mp3 is shipped, so this default is safe even
    // before any track is committed.
    music_enabled: d.music_enabled === false ? false : true,
    // Color palette. Defaults to 'rose' (the original brand). Unknown
    // values fall back to 'rose' so a typo or stale row never strands
    // the UI on an undefined :root[data-theme=...] selector.
    theme: THEMES.includes(d.theme) ? d.theme : 'rose',
    // Cycle tracking inputs. Both written from CycleSettingsTile and
    // (for the date) the legacy Profile field. Surfaced to the
    // CycleArc + Calendar so phase + forecast survive a round-trip
    // refetch (without these here, normalizeUserProgress would strip
    // them and the cycle UI would silently regress to its empty state).
    cycle_last_period_start: typeof d.cycle_last_period_start === 'string' ? d.cycle_last_period_start : null,
    cycle_length: Number.isFinite(d.cycle_length) ? d.cycle_length : null,
    cycle_period_length: Number.isFinite(d.cycle_period_length) ? d.cycle_period_length : null,
    // Self-declared menopause stage from MenopauseStageTile. One of
    // 'pre' | 'peri' | 'meno' | 'post' | 'unsure', or null when the
    // user has not answered yet. Drives copy + UI mode (e.g. dim the
    // cycle arc once cycles become irregular). Unknown values are
    // coerced to null so a stale or typo'd row never strands the UI.
    // Self-declared life stage from onboarding (PersonalizeStep). Preserved so
    // it survives a refetch and stays the source the bridge maps from.
    life_stage: typeof d.life_stage === 'string' ? d.life_stage : null,
    // menopause_stage drives all life-stage UI behaviour. An explicit value from
    // the Profile tile wins; otherwise it is derived from the onboarding
    // life_stage answer so that choice is not orphaned (see resolveMenopauseStage).
    menopause_stage: resolveMenopauseStage(d),
    // IntentPicker (first onboarding step) writes one of postpartum /
    // pre_surgery / intimacy / prevention / noticing / unspecified.
    // WeekHome reads this off React state to render the italic chip
    // under the home greeting; without it here, the normalizer would
    // strip the field and the chip silently never renders.
    user_intent: typeof d.user_intent === 'string' ? d.user_intent : 'unspecified',
    // Per-screen "How clear is that?" signals captured by the felt-
    // sense check on FoundationLesson. Stored as a sparse map
    // { "0": "clear", "8": "not_yet", ... } so back-stepping inside
    // the lesson re-seeds the chip selection and FoundationLessonsList
    // can surface a "Revisit" pill on cards the user marked unclear.
    // Without this in the normalizer the field would survive in
    // localStorage / Supabase but get stripped on every refetch — the
    // chip + pill would silently reset on every page reload.
    fl_signals: (d.fl_signals && typeof d.fl_signals === 'object' && !Array.isArray(d.fl_signals))
      ? d.fl_signals
      : {},
    // Daily libido / desire signal logged from /body. Sparse map of
    // { [iso_date]: 0-3 }. Visible on every life stage so it works
    // for cycling, peri, and menopause users alike. Stripped on
    // refetch unless preserved here.
    libido_log: (d.libido_log && typeof d.libido_log === 'object' && !Array.isArray(d.libido_log))
      ? d.libido_log
      : {},
  };
}

/**
 * True when the user has a path assigned and has reached week mode.
 * This is the "post-onboarding" signal used by AppContext to skip the
 * legacy onboarding sequence. Matches the old inline checkOnboardingFlags.
 *
 * @param {Object|null|undefined} raw - The raw `data` column payload.
 * @returns {boolean}
 */
export function checkOnboardingComplete(raw) {
  if (!raw || typeof raw !== 'object') return false;
  return !!(raw.path && (raw.currentWeek || raw.current_week));
}
